// Responsive sidebar panel item for the library page

import React from 'react';
import { ChevronRightIcon } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
} from '../../components/ui/sidebar';
import { cn } from '@/utilities/cn';

type SidebarPanelItemProps = {
  isMobile: boolean;
  collapsedDesktop: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  children: React.ReactNode;
};

/**
 * Shared desktop popover / mobile collapsible pattern for sidebar preference panels.
 */
const SidebarPanelItem: React.FC<SidebarPanelItemProps> = ({
  isMobile,
  collapsedDesktop,
  open,
  onOpenChange,
  title,
  icon,
  label,
  isActive,
  children,
}) => {
  const mobilePanelSurface = (
    <div className="flex flex-col gap-4 rounded-xl border border-sidebar-border bg-background p-4 shadow-sm">
      {children}
    </div>
  );

  if (isMobile) {
    return (
      <Collapsible
        open={open}
        onOpenChange={onOpenChange}
        className="group/collapsible"
      >
        <CollapsibleTrigger asChild>
          <SidebarMenuButton isActive={isActive}>
            {icon}
            <span>{label}</span>
            <ChevronRightIcon
              className={cn(
                'ml-auto transition-transform',
                open && 'rotate-90'
              )}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <SidebarMenuSub>
            <SidebarMenuSubItem>{mobilePanelSurface}</SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          isActive={isActive}
          className={collapsedDesktop ? 'justify-center px-2' : ''}
          title={title}
        >
          {icon}
          {!collapsedDesktop && <span>{label}</span>}
          {!collapsedDesktop && <ChevronRightIcon className="ml-auto" />}
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="right"
        sideOffset={8}
        className="w-80"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};

export default SidebarPanelItem;
